<template>
  <mdb-container v-if="loggedIn" class="gp-has-header">
    <translate-section v-if="translationMode" category="page" sub-category="profile"/>

    <div v-if="profile">
      <mdb-card class="mb-4" v-if="!profile.country || !profile.city">
        <mdb-card-header class="danger-color text-white"><h5>{{$t("page.profile.incompleteProfile")}}</h5></mdb-card-header>
        <mdb-card-body>
          <div v-if="!profile.country && !profile.city">
            {{ $t("page.profile.cityAndCountryMissing") }}
          </div>
          <div v-if="profile.country && !profile.city">
            {{ $t("page.profile.cityMissing") }}
          </div>
          <div v-if="!profile.country && profile.city">
            {{ $t("page.profile.countryMissing") }}
          </div>
        </mdb-card-body>
      </mdb-card>

      <h3 class="text-left"><img :src="profile.avatar" alt="avatar image"
                                 class="rounded-circle z-depth-0"
                                 style="width:30px;"/>{{ $t("page.profile.myProfile") }}</h3>
      <div> <!-- mdb-tabs is not i18n friendly :-( -->
        <ul class="nav nav-tabs md-tabs">
          <li class="nav-item"><a :class="'nav-link ripple-parent' + (tabContent['basics'] == true ? ' active' : '')"
                                  href="#" @click.prevent="switchTab('basics')">{{ $t("page.profile.basics") }}</a></li>
          <li class="nav-item" v-if="showAdditionalDetails"><a
            :class="'nav-link ripple-parent' + (tabContent['additionalDetails'] == true ? ' active' : '')" href="#"
            @click.prevent="switchTab('additionalDetails')">{{ $t("page.profile.additionalDetails") }}</a></li>
          <li class="nav-item"><a :class="'nav-link ripple-parent' + (tabContent['picture'] == true ? ' active' : '')"
                                  href="#" @click.prevent="switchTab('picture')">{{ $t("page.profile.picture") }}</a>
          </li>
          <li class="nav-item" v-if="showChangeSkin"><a
            :class="'nav-link ripple-parent' + (tabContent['lookAndFeel'] == true ? ' active' : '')" href="#"
            @click.prevent="switchTab('lookAndFeel')">{{ $t("page.profile.lookAndFeel") }}</a></li>
          <li class="nav-item"><a :class="'nav-link ripple-parent' + (tabContent['timeZone'] == true ? ' active' : '')"
                                  href="#" @click.prevent="switchTab('timeZone')">{{ $t("page.profile.timeZone") }}</a>
          </li>
          <li class="nav-item"><a
            :class="'nav-link ripple-parent' + (tabContent['introduction'] == true ? ' active' : '')" href="#"
            @click.prevent="switchTab('introduction')">{{ $t("page.profile.introduction") }}</a></li>
          <li class="nav-item"><a :class="'nav-link ripple-parent' + (tabContent['calendar'] == true ? ' active' : '')"
                                  href="#" @click.prevent="switchTab('calendar')">{{ $t("page.profile.calendar") }}</a>
          </li>
          <li class="nav-item"><a
            :class="'nav-link ripple-parent' + (tabContent['deleteAccount'] == true ? ' active' : '')" href="#"
            @click.prevent="switchTab('deleteAccount')">{{ $t("page.profile.deleteAccount") }}</a></li>
        </ul>
      </div>

      <div>
        <mdb-card class="mb-4" v-if="isEditing && tabContent['basics'] == true">
          <mdb-card-body>
            <form novalidate class="text-left" @keypress.13.prevent @submit.prevent="saveDetails">
              <mdb-input v-if="!settings.identityProvider=='COGNITO'" v-model="editing.username"
                         :label="capitalize($t('common.label.username'))" required/>
              <mdb-input v-model="editing.name" :label="capitalize($t('common.label.name'))" required/>
              <div class="text-info">
                {{ $t('page.profile.commonNameHelp') }}
              </div>
              <mdb-input v-model="editing.email" :label="capitalize($tc('common.label.emailAddress',1))" required/>
              <gp-select :dataObj="editing" :possible-values="countries" :required="true"
                         :label="capitalize($tc('common.label.country',1))"
                         propertyName="country"/>
              <mdb-input v-model="editing.city" :label="capitalize($tc('common.label.city',1))" required/>
              <mdb-input v-model="editing.postal" :label="capitalize($t('common.label.postalCode'))"/>
              <gp-select :dataObj="editing" :possible-values="dateFormats" :required="true"
                         :label="capitalize($t('common.label.dateFormat'))" propertyName="dateFormat"/>
              <gp-select :dataObj="editing" :possible-values="timeFormats" :required="true"
                         :label="capitalize($t('common.label.timeFormat'))" propertyName="timeFormat"/>

              <div class="md-form text-left">
                <select v-model="editing.firstDayOfWeek"
                        class="gp-custom-select">
                  <option value="Sunday">{{ $t("common.day.sunday") }}</option>
                  <option value="Monday">{{ $t("common.day.monday") }}</option>
                </select>
                <label class="active">{{$t('common.label.firstDayOfWeek')}}</label>
              </div>

              <gp-select :dataObj="editing" :possible-values="decimalFormats" :required="true"
                         :label="capitalize($t('common.label.decimalSeparator'))" propertyName="decimalSeparator"/>
              <div class="text-center">
                <mdb-btn type="submit" color="success" size="sm" rounded icon="save">
                  {{$t("common.button.save")}}
                </mdb-btn>
                <mdb-btn @click="cancelEdit" color="danger" size="sm" rounded icon="minus-circle">
                  {{$t("common.button.cancel")}}
                </mdb-btn>
              </div>
              <div v-if="formErrors" class="text-danger">
                {{ formErrors }}
              </div>
            </form>
          </mdb-card-body>
        </mdb-card>

        <mdb-card class="mb-4" v-if="!isEditing && tabContent['basics'] == true">
          <mdb-card-body>
            <mdb-input v-if="!settings.identityProvider=='COGNITO'" v-model="profile.username" disabled
                       label="Username"/>
            <mdb-input v-model="profile.name" disabled :label="capitalize($t('common.label.name'))"/>
            <mdb-input v-model="profile.email" disabled :label="capitalize($tc('common.label.emailAddress',1))"/>
            <mdb-input v-model="displayCountry" disabled :label="capitalize($tc('common.label.country',1))"/>
            <mdb-input v-model="profile.city" disabled :label="capitalize($tc('common.label.city',1))"/>
            <mdb-input v-model="profile.postal" disabled :label="capitalize($t('common.label.postalCode'))"/>
            <mdb-input v-model="profile.dateFormat" disabled :label="capitalize($t('common.label.dateFormat'))"/>
            <mdb-input v-model="profile.timeFormat" disabled :label="capitalize($t('common.label.timeFormat'))"/>
            <div class="md-form text-left">
              <select v-model="profile.firstDayOfWeek" disabled
                      class="gp-custom-select">
                <option value="Sunday">{{ $t("common.day.sunday") }}</option>
                <option value="Monday">{{ $t("common.day.monday") }}</option>
              </select>
              <label class="active">{{$t('common.label.firstDayOfWeek')}}</label>
            </div>

            <mdb-input v-model="profile.decimalSeparator" disabled :label="capitalize($t('common.label.decimalSeparator'))"/>
            <mdb-btn icon="pencil-alt" rounded size="sm" @click="editDetails"> {{$t("common.button.edit")}}</mdb-btn>
            <mdb-btn icon="lock" rounded size="sm" @click="openPasswordChange"> {{$t("common.button.changePassword")}} </mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </div>

      <div v-if="tabContent['picture'] == true">
        <mdb-card class="mb-4">
          <mdb-card-body>
            <img :src="profile.avatar" class="rounded-circle">
            <div class="text-center">
              <mdb-btn icon="upload" rounded size="sm" @click="showUpload=true"> {{$t("common.button.new")}}</mdb-btn>
              <mdb-btn v-if="profile.avatarCount > 1" icon="image" rounded size="sm" @click="changeAvatar"> {{$t("common.button.change")}}
              </mdb-btn>
            </div>
          </mdb-card-body>
        </mdb-card>
      </div>

      <div v-if="tabContent['additionalDetails'] == true">
        <mdb-card class="mb-4">
          <mdb-card-body>
            <custom-data :custom-data="profile.customFields" allow-edit @dataSaved="customFieldsSaved"/>
          </mdb-card-body>
        </mdb-card>
      </div>
      <div v-if="tabContent['lookAndFeel'] == true">
        <mdb-card class="mb-4" v-if="profile.userMayChangeSkin">
          <mdb-card-body class="text-left">
            <gp-select :dataObj="profile" :possible-values="skins" :required="true"
                       @valueChanged="skinChanged"
                       label="Color Scheme" propertyName="skin"/>
            <div class="small">
              {{ $t("page.profile.skinText", {siteName: settings.siteName})}}
            </div>
          </mdb-card-body>
        </mdb-card>
      </div>
      <div v-if="tabContent['timeZone'] == true">
        <mdb-card class="mb-4">
          <mdb-card-body>
            <p><b><i>{{ profile.timezone }}</i></b></p>
            <p>{{ $t("page.profile.timeZoneText", {siteName: settings.siteName, timezone: browserTimezone})}}</p>
            <mdb-btn icon="clock" rounded size="sm" @click="showTimezoneSelector=true"> {{$t("common.button.change")}}</mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </div>
      <div v-if="tabContent['introduction'] == true">
        <mdb-card class="mb-4">
          <mdb-card-body>
            <span>
              <mdb-icon icon="quote-left"/>
            </span>
            <div v-html="profile.introduction"></div>
            <mdb-btn icon="pencil-alt" rounded size="sm" @click="openIntro"> {{$t("common.button.edit")}}</mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </div>
      <div v-if="tabContent['calendar'] == true">
        <mdb-card class="mb-4">
          <mdb-card-body>
            <div v-if="!profile.calendarUrl">
              <p class="text-left">{{$t("page.profile.calendarText")}}</p>
              <mdb-btn icon="calendar" rounded size="sm" @click="genGuid">{{$t("page.profile.getCalendarLink")}}</mdb-btn>
            </div>
            <div v-if="profile.calendarUrl" class="text-left">
              <p class="text-left">{{$t("page.profile.calendarLink")}}</p>
              <p><input id="calendar-url" v-model="profile.calendarUrl" style="width:100%"/></p>
              <p>
                <mdb-btn icon="copy" rounded size="sm" @click="copyClipboard">{{$t("common.button.copyToClipboard")}}</mdb-btn>
                <mdb-badge v-if="urlCopied">{{$t("common.label.copied")}}!</mdb-badge>
              <p class="text-left">{{$t("page.profile.calendarDisclaimer")}}</p>
            </div>
          </mdb-card-body>
        </mdb-card>
      </div>
      <div v-if="tabContent['deleteAccount'] == true">
        <mdb-card class="mb-4">
          <mdb-card-body>
            <mdb-btn color="danger" @click="showDeleteProfileModal = true">{{$t("page.profile.deleteText")}}</mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </div>
    </div>
    <mdb-modal :show="showUpload " size="lg" @close="showUpload=false">
      <mdb-modal-header :close="true">
        <h1>{{$t("common.label.uploadImage")}}</h1>
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-file-input v-on:getValue="uploadFile"/>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal :show="showAvatarSelector" size="lg" @close="showAvatarSelector = false">
      <mdb-modal-header :close="true" class="secondary-color">
        {{$t("common.label.selectImage")}}
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-row>
          <mdb-col v-for="image in allAvatars" v-if="image.url != profile.avatar" v-bind:key="image.id" class="mb-4"
                   col="4">
            <img :src="image.url" class="rounded-circle" style="cursor:pointer" width="100%"
                 @click="selectAvatar(image)">
            <mdb-btn color="danger" floating
                     icon="trash"
                     tag="a" type="" @click="deleteAvatar(image)"/>
          </mdb-col>
        </mdb-row>
      </mdb-modal-body>
    </mdb-modal>
    <mdb-modal v-if="introEditor && profile" @close="cancelIntro">
      <mdb-modal-header :close="true">
        {{$t("page.profile.introduceYourself")}}
      </mdb-modal-header>
      <mdb-modal-body>
        <editor v-model="profile.introduction" :init="editorConfig">
        </editor>
        <mdb-btn @click="saveIntro">
          <mdb-icon icon="save"/>
          {{$t("common.button.save")}}
        </mdb-btn>
        <mdb-btn @click="cancelIntro">
          <mdb-icon icon="window-close"/>
          {{$t("common.button.cancel")}}
        </mdb-btn>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="passwordChange" @close="passwordChange=false">
      <mdb-modal-header :close="true" class="secondary-color">
        {{$t("common.label.changePassword")}}
      </mdb-modal-header>
      <mdb-modal-body>
        <form class="text-left needs-validation" @keypress.13.prevent @submit.prevent="savePassword">
          <mdb-input id="new-password" v-model="newPassword" label="New Password" required type="password"/>
          <mdb-input v-model="newPasswordConfirm" label="Confirm" required type="password"/>
          <div v-if="newPassword!=newPasswordConfirm || newPassword.length<6" class="text-danger">Passwords must match
            and be at least 6 characters long.
          </div>
          <mdb-btn :disabled="newPassword!=newPasswordConfirm || newPassword.length<6" type="submit">
            <mdb-icon icon="save"/>
            {{$t("common.button.save")}}
          </mdb-btn>
          <mdb-btn @click="passwordChange=false">
            <mdb-icon icon="window-close"/>
            {{$t("common.button.cancel")}}
          </mdb-btn>
        </form>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="showTimezoneSelector" @close="showTimezoneSelector=false">
      <mdb-modal-header :close="true">
        Select Your Time Zone
      </mdb-modal-header>
      <mdb-modal-body>
        <timezone-select :clickHandler="timezoneSelected"></timezone-select>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="showDeleteProfileModal" @close="showDeleteProfileModal = false">
      <mdb-modal-header :close="true" class="primary-color text-white">
        <h5>{{$t("page.profile.deleteAccount")}}</h5>
      </mdb-modal-header>
      <mdb-modal-body class="text-left">
        <h5>{{$t("common.label.areYouSure")}}</h5>
        <p>If you are sure you wish to permanently remove your account, here's what will happen:</p>
        <ol>
          <li>We will remove you from all your groups and future events. Group admins will be notified that you left the group.</li>
          <li>We will immediately disable your account and you will not be able to re-enable it yourself.</li>
          <li>After 2 days of your account being disabled, we will remove it <i>completely</i> from our database,
            and this action is not recoverable.
          </li>
          <li>If during the 2 day disabled period you change your mind, you may send an email to
            {{ settings.supportEmail }}
            asking to reinstate your account.
          </li>
        </ol>
        <h5>Are you <i>really</i> sure?</h5>

      </mdb-modal-body>
      <mdb-modal-footer class="text-center">
        <mdb-btn color="danger" icon="trash" @click="deleteProfile">{{$t("page.profile.deleteConfirm")}}</mdb-btn>
        <mdb-btn color="success" icon="times-cirle" @click="showDeleteProfileModal = false">{{$t("page.profile.deleteCancel")}}</mdb-btn>
      </mdb-modal-footer>
    </mdb-modal>

    <yes-no-modal v-if="deleteModal" @no="deleteModal=false" @yes="doDelete"/>

  </mdb-container>

</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import bannerSmall from '../group/BannerSmall'
import gpSelect from '../common/GPSelect'
import iso from "iso-3166-1"
import editor from '@tinymce/tinymce-vue'
import timezone from '../../services/timezone'
import timezoneSelect from '../common/TimezoneSelect'
import YesNoModal from "../common/YesNoModal";
import CustomData from "../common/CustomData";

import {
  mdbBadge,
  mdbBtn,
  mdbCard,
  mdbCardAvatar,
  mdbCardBody,
  mdbCardHeader,
  mdbCardTitle,
  mdbChipInput,
  mdbCol,
  mdbContainer,
  mdbFileInput,
  mdbIcon,
  mdbInput,
  mdbModal,
  mdbModalBody,
  mdbModalFooter,
  mdbModalHeader,
  mdbRow,
  mdbTabs,
  mdbTextarea,
  mdbTooltip
} from 'mdbvue';
import Vue from "vue";
import TranslateSection from "../common/TranslateSection";
import TranslateModal from "../common/TranslateModal";

export default {
  name: "user-profile",
  display: "User Profile",
  order: 1,
  components: {
    mdbContainer,
    mdbBadge,
    mdbBtn,
    mdbIcon,
    mdbCard,
    mdbCardHeader,
    mdbCardAvatar,
    mdbCardBody,
    mdbCardTitle,
    mdbTooltip,
    mdbInput,
    mdbRow,
    mdbCol,
    mdbChipInput,
    mdbTextarea,
    mdbFileInput,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbModalFooter,
    mdbTabs,
    bannerSmall,
    gpSelect,
    editor,
    timezoneSelect,
    YesNoModal,
    CustomData,
    TranslateModal,
    TranslateSection
  },

  computed: {
    loggedIn() {
      return this.$store.state.loggedIn
    },
    language() {
      return this.$store.state.language
    },
    userDetails() {
      return this.$store.state.userDetails
    },
    settings() {
      return this.$store.state.settings
    },
    translationMode() {
      return this.$store.state.translationMode
    },
    displayCountry() {
      if (!this.profile) return ""
      if (!this.profile.country) return ""
      for (let c of this.countries) {
        if (c.value == this.profile.country) return c.text
      }
      return ""
    }
  },
  watch: {
    language: function (n, o) {
      if (n) {
        for (let fd of this.firstDays) {
          if (fd.value == "Sunday") fd.text = this.$t("common.day.sunday")
          if (fd.value == "Monday") fd.text = this.$t("common.day.monday")
        }
      }
    }
  },
  data() {
    return {
      profile: null,
      urlCopied: false,
      browserTimezone: null,
      showUpload: false,
      activeTab: 0,
      showTimezoneSelector: false,
      allAvatars: [],
      showAvatarSelector: false,
      introEditor: false,
      detailsEditor: false,
      passwordChange: false,
      newPassword: "",
      newPasswordConfirm: "",
      oldDetails: null,
      oldIntro: null,
      isEditing: false,
      editing: null,
      countries: [],
      skins: [],
      decimalFormats: [{text: ", (Comma)", value: ","}, {text: ". (Dot)", value: "."}],
      timeFormats: [{text: "24 Hour", value: "24 Hour"}, {text: "AM/PM", value: "AM/PM"}],
      dateFormats: [{text: "MM/dd/yyyy", value: "MM/dd/yyyy"}, {text: "dd-MM-yyyy", value: "dd-MM-yyyy"},
        {text: "dd-MMM-yyyy", value: "dd-MMM-yyyy"}],
      firstDays: [{text: "Sunday", value: "Sunday"}, {text: "Monday", value: "Monday"}],
      editorConfig: {
        browser_spellcheck: true,
        branding: false,
        plugins: ["link", "code", "paste", "table", "lists"],
        toolbar: ["undo redo cut copy paste bold italic underline numlist bullist fontsizeselect | alignleft alignright aligncenter | link unlink code "],
        style_formats_merge: true,
        paste_as_text: true,
        relative_urls: false,
        remove_script_host: false,
        height: 400
      },
      deleteModal: false,
      showDeleteProfileModal: false,
      deleteCandidate: null,
      formErrors: "",
      tabContent: {
        basics: true,
        picture: false,
        additionalDetails: false,
        lookAndFeel: false,
        timeZone: false,
        introduction: false,
        calendar: false,
        deleteAccount: false
      },
      showChangeSkin: false,
      showAdditionalDetails: false


    }
  },
  beforeMount() {
    this.browserTimezone = timezone.detectTimeZone()
    for (let i = 0; i < iso.all().length; i++) {
      let row = iso.all()[i]
      let c = {text: row.country, value: row.alpha3.toLowerCase()}
      this.countries.push(c)
    }
  },
  async mounted() {
    document.title = "My Profile"
    if (this.$route.query.tab) {
      this.switchTab(this.$route.query.tab)
    }
    await this.$store.dispatch("setExecuting", true)
    await api.get('/user/profile').then(
      (details) => {
        this.profile = details.data.user
        if (this.profile.userMayChangeSkin) this.showChangeSkin = true
        if (this.profile.customFields.length > 0) this.showAdditionalDetails = true
        if (details.data.skins && details.data.skins.length > 0) {
          for (let skin of details.data.skins) {
            let label = skin.charAt(0).toUpperCase() + skin.slice(1)
            label = label.replace("-skin", "")
            label = label.replace("-cust", "")
            label = label.replace("-", " ")
            let row = {text: label, value: skin}
            this.skins.push(row)
          }
        }
        console.log("SSKSKSS", this.skins)
        if (this.$route.query.forceChange == "Y") this.openPasswordChange()
      },
      (error) => {
        if (error.response.status == 403 || error.response.status == 401) this.$router.push("/403")
        else this.$notify.error({message: "Error getting profile data."})
      }).then(() => {
      this.$store.dispatch("setExecuting", false)
    })

    api.get('/public/countryList?sort=desc').then(
      (details) => {
        for (let c of details.data) {
          let country = {text: c.name, value: c.alpha3Code.toLowerCase()}
          this.countries.unshift(country)
        }
      },
      (error) => {
        console.error("Error loading in-use countries.", error)
      })

  },
  methods: {
    switchTab(val) {
      console.log("Switch to ", val)
      for (let key in this.tabContent) {
        console.log("Key is ", key)
        if (key == val) this.tabContent[key] = true
        else this.tabContent[key] = false
      }
    },
    skinChanged(val) {
      api.post("/user/selectSkin", {skin: val}).then(
        () => {
          document.body.className = ""
          document.body.classList.add(val)
        },
        (error) => {
          this.$notify.error({message: "Could not change at this time.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    openPasswordChange() {
      this.passwordChange = true
      Vue.nextTick().then(function () {
        let element = document.getElementById("new-password");
        if (element) element.focus();
      })
    },
    deleteAvatar(image) {
      this.deleteCandidate = image
      this.deleteModal = true
    },
    doDelete() {
      console.log("Delete image", this.deleteCandidate)
      this.deleteModal = false
      this.showAvatarSelector = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/user/deleteAvatar/" + this.deleteCandidate.id).then(
        (results) => {
          let loc = this.allAvatars.indexOf(this.deleteCandidate)
          this.allAvatars.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: "Could not delete images at this time.", timeOut: 5000})
        }).then(() => {
        this.showAvatarSelector = true
        this.$store.dispatch("setExecuting", false)
      })
    },
    timezoneSelected: function (tz) {
      this.showTimezoneSelector = false
      api.post("/user/timezone", {timezone: tz}).then(
        (results) => {
          this.profile.timezone = tz
          this.$notify.success({message: "Timezone changed successfully.", timeOut: 3000})
        },
        (error) => {
          this.$notify.error({message: "Could not change at this time.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    uploadFile: async function (file) {
      this.showUpload = false
      let toUpload = file[0]
      await this.$store.dispatch("setExecuting", true)
      const reduce = require('image-blob-reduce')();
      if (file[0].size >= 2000000) {
        await reduce.toBlob(file[0], {max: 2000})
          .then(blob => {
            toUpload.file = blob
          })
      }

      let uploadUrl
      let uploadRequest
      await api.post("/user/getImageUploadUrl", {filename: file[0].name,
                             size: file[0].size,
                             contentType: file[0].type}).then(
        (results) => {
          uploadRequest = results.data
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
          this.$store.dispatch("setExecuting", false)
        }
      )

      if (!uploadRequest) return

      const response = await fetch(uploadRequest.url, {
        method: 'PUT',
        body: file[0],
        headers: {
          'Content-Type': file[0].type
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        this.$notify.error({message: errorText, timeOut: 5000})
        this.$store.dispatch("setExecuting", false)
        return

      }

      api.post("/user/uploadAvatar", uploadRequest).then(
        (results2) => {
          this.profile.avatar = results2.data.avatar
          this.$store.dispatch("setAvatar", this.profile.avatar)
          this.$notify.success({message: "Avatar uploaded successfully.", timeOut: 3000})
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    changeAvatar: function () {
      this.$store.dispatch("setExecuting", true)
      api.get("/user/listAvatars").then(
        (results) => {
          this.allAvatars = results.data
          this.showAvatarSelector = true
        },
        (error) => {
          this.$notify.error({message: "Could not select images at this time.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    selectAvatar: function (image) {
      this.showAvatarSelector = false
      api.post("/user/selectAvatar", image).then(
        (results) => {
          this.profile.avatar = results.data.avatar
          this.$store.dispatch("setAvatar", this.profile.avatar)
          this.$notify.success({message: "Avatar changed successfully.", timeOut: 3000})
        },
        (error) => {
          this.$notify.error({message: "Could not change at this time.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    openIntro: function () {
      this.oldIntro = this.profile.introduction
      this.introEditor = true
    },
    saveIntro: function () {
      this.introEditor = false
      api.post("/user/introduction", {introduction: this.profile.introduction}).then(
        (results) => {
          this.$notify.success({message: "Introduction changed successfully.", timeOut: 3000})
        },
        (error) => {
          this.$notify.error({message: "Could not change at this time.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    cancelIntro: function () {
      this.profile.introduction = this.oldIntro
      this.introEditor = false
    },
    editDetails: function () {
      this.editing = JSON.parse(JSON.stringify(this.profile));
      this.isEditing = true
      this.$nextTick(() => {
        let el = document.getElementsByClassName("tab-content")
        if (el && el.length > 0) {
          el[0].setAttribute("style", "")  //remove previously calculated height; mdb-tab doesn't resize with content!
        }
      });
    },
    cancelEdit: function () {
      this.editing = null
      this.isEditing = false
    },
    saveDetails: function (event) {
      if (!event.target.checkValidity()) {
        event.target.classList.add("was-validated")
        this.formErrors = "Please correct form errors and try again."
        return
      }
      this.formErrors = ""
      this.isEditing = false
      api.post("/user/setDetails", this.editing).then(
        (results) => {
          this.$store.dispatch("setProfileReview", false)
          this.$notify.success({message: "Details changed successfully.", timeOut: 3000})
          this.profile = this.editing
          api.reloadUser()
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
          this.isEditing = true
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    savePassword: function () {
      this.passwordChange = false
      if (this.settings.identityProvider == "COGNITO") {
        this.$store.dispatch("setExecuting", true);
        api.post("/user/cognitoResetPassword", {password: this.newPassword}).then(
          (details) => {
            this.$notify.success({message: "Password changed successfully.", timeOut: 5000})
          },
          (error) => {
            this.$notify.error({message: error.response.data, timeOut: 15000})
            this.passwordChange = true
          }).then(() => {
                    this.$store.dispatch("setExecuting", false);
                  }
        )
      }
      if (this.settings.identityProvider == "LOCAL") {
        api.post("/user/setPassword", {password: this.newPassword}).then(
          (results) => {
            this.$notify.success({message: "Password changed successfully.", timeOut: 3000})
            api.reloadUser()
          },
          (error) => {
            this.$notify.error({message: error.response.data, timeOut: 5000})
            this.passwordChange = true
          }).then(() => {
          this.$store.dispatch("setExecuting", false)
        })
      }
    },
    genGuid: function () {
      api.get("/user/calGuid").then(
        (results) => {
          this.profile.calendarUrl = results.data
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deleteProfile: function () {
      this.showDeleteProfileModal = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/user/deleteAccount").then(
        (results) => {
          this.$store.dispatch("logout")
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    copyClipboard: function () {
      let el = document.getElementById("calendar-url")
      if (!el) return
      el.select()
      el.setSelectionRange(0, 99999);
      document.execCommand("copy")
      this.urlCopied = true
    },
    customFieldsSaved: function (fields) {
      this.$store.dispatch("setExecuting", true)
      api.post("/user/myCustomFields", fields).then(
        (results) => {
          this.profile.customFields = results.data
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    }

  }

};
</script>

<style scoped>
.toolbar-buttons {
  float: right;
}
</style>
